import { useEntranceGUIDParam } from '@/features/entrance/useEntranceGUIDParam'
import type { MatchEntrance, RoomID } from '@/types/app'
import { computed, type ComputedRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const getRouteType = (
  routeName: unknown
): Maybe<'own' | 'member' | 'assigned'> => {
  switch (routeName) {
    case 'ownChatIndex':
    case 'ownChat':
      return 'own'
    case 'memberChatIndex':
    case 'memberChat':
      return 'member'
    case 'assignedChatIndex':
    case 'assignedChat':
      return 'assigned'
  }
  return
}

export const useChatPageRouteControl = (
  selectedRoomId: ComputedRef<Maybe<number>>
) => {
  const route = useRoute()
  const router = useRouter()

  const { entranceGUID } = useEntranceGUIDParam({ must: true })
  const activeView = computed(() => getRouteType(route.name))
  const isMemberRoute = computed((): boolean => activeView.value === 'member')
  const isAssignedRoute = computed(
    (): boolean => activeView.value === 'assigned'
  )

  const nextRouteTo = (roomId: RoomID) => {
    if (isAssignedRoute.value) {
      return {
        name: 'assignedChat',
        params: {
          roomId: roomId.toString(),
          entranceGUID: entranceGUID.value,
        },
      }
    }

    if (isMemberRoute.value) {
      return {
        name: 'memberChat',
        params: {
          roomId: roomId.toString(),
          entranceGUID: entranceGUID.value,
        },
      }
    }

    return {
      name: 'ownChat',
      params: {
        roomId: roomId.toString(),
        entranceGUID: entranceGUID.value,
      },
    }
  }

  const selectRoom = async (roomId: RoomID) => {
    if (selectedRoomId.value === roomId) {
      return
    }
    // eslint-disable-next-line fp/no-mutating-methods, @typescript-eslint/no-explicit-any
    await router.push(nextRouteTo(roomId) as any)
  }
  const selectMatch = async (match: MatchEntrance) => {
    await selectRoom(match.roomId)
  }

  return {
    selectedRoomId,
    selectMatch,
    isMemberRoute,
    activeView,
  }
}
